import { Card } from '@progress/kendo-react-layout';
import { BaseButton } from 'app/components/buttons/BaseButton';
import { ActionsListItem, EllipsisMenu } from 'app/components/menu';
import useHasAccessSharingPermissions from 'app/hooks/useHasAccessSharingPermissions';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { SharedItem } from 'services/api/shares';
import capitalizeFirstLetter from 'utils/text/capitalizeFirstLetter';

interface ISharedFleetsRow {
  fleet: SharedItem;
  isMobile: boolean;
  onEditFleet: () => void;
  onEditFleetName: () => void;
  onDeleteFleet: () => void;
  onRevokeOwnAccess: () => void;
}

function SharedFleetsRow({
  fleet,
  isMobile,
  onEditFleet,
  onEditFleetName,
  onDeleteFleet,
  onRevokeOwnAccess,
}: ISharedFleetsRow) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const hasAccess = useHasAccessSharingPermissions(
    fleet.sharedItemDetails.myRole,
  );

  const actions = useMemo<ActionsListItem[]>(() => {
    return [
      {
        label: t('access_sharing.role_based_actions.add_new_user'),
        action: () =>
          navigate(
            `/access/shared-fleet/${fleet.sharedItemDetails.exposedId}/add-new-user`,
          ),
        exclude: !hasAccess('addNewUser'),
      },
      {
        label: t('access_sharing.role_based_actions.edit_fleet'),
        action: onEditFleet,
        exclude: !hasAccess('editFleet'),
      },
      {
        label: t('access_sharing.role_based_actions.edit_fleet_name'),
        action: onEditFleetName,
        exclude: !hasAccess('editFleetName'),
      },
      {
        label: t('access_sharing.role_based_actions.delete_fleet'),
        action: onDeleteFleet,
        exclude: !hasAccess('deleteFleet'),
      },
      // {
      //   label: t('access_sharing.role_based_actions.view_logs'),
      //   action: () => {},
      //   exclude: !hasAccess('viewLogs'),
      // },
      {
        label: t('access_sharing.role_based_actions.revoke_my_access'),
        action: onRevokeOwnAccess,
        exclude: !hasAccess('revokeOwnAccess'),
      },
    ];
  }, [hasAccess, t]);

  function handleOpenDetailsPage() {
    navigate('/access/shared-fleet/' + fleet.sharedItemDetails.exposedId);
  }

  return (
    <>
      {!isMobile ? (
        <BaseButton
          className="access-sharing-table-row"
          onClick={handleOpenDetailsPage}
        >
          <div>
            <span className="equipment-nickname ellipsis-2-lines">
              {fleet.sharedItemDetails.sharedItemLabel}
            </span>
            <span>
              {t('number_of_equipment', {
                numberOfEquipment: fleet.sharedItemDetails.exposedEquipmentIds
                  ? fleet.sharedItemDetails.exposedEquipmentIds.length
                  : 0,
              })}
            </span>
          </div>
          <div className="pending-details">
            {t(fleet.sharedItemDetails.myRole.toLowerCase()).toUpperCase()}
          </div>
          <div className="equipment-table-field ellipsis">
            {fleet.sharedItemDetails.ownerName}
          </div>
          <div className="equipment-table-field ellipsis">
            {fleet.sharedItemDetails.ownerEmail}
          </div>
          <div className="pending-details">
            {!!fleet.sharedWith.length
              ? t('shared_with_users', {
                  users: fleet.sharedWith.length,
                })
              : null}
          </div>

          <EllipsisMenu actions={actions} />
        </BaseButton>
      ) : (
        <div
          className="access-sharing-table-card cursor-pointer fleet"
          onClick={handleOpenDetailsPage}
        >
          <div className="row">
            <div className="col-xxs-12">
              <span className="equipment-nickname ellipsis-2-lines">
                {fleet.sharedItemDetails.sharedItemLabel}
              </span>
              <span className="number-of-eq-info">
                {t('number_of_equipment', {
                  numberOfEquipment: fleet.sharedItemDetails.exposedEquipmentIds
                    ? fleet.sharedItemDetails.exposedEquipmentIds.length
                    : 0,
                })}
              </span>
            </div>
          </div>
          <div className="equipment-list-details">
            <div className="row-4">
              <div className="col-xxs-12">
                <Card className="access-sharing-equipment-details">
                  <div>
                    <span className="bolded-info">{t('owner')}</span>
                    <span className="vertical-line"></span>
                    <span className="bolded-info">
                      {fleet.sharedItemDetails.ownerName} (Me)
                    </span>
                  </div>
                  <div className="ellipsis">
                    {fleet.sharedItemDetails.ownerEmail}
                  </div>
                </Card>
              </div>
            </div>
            <div className="row-4">
              <div className="col-xxs-6">
                <Card className="access-sharing-equipment-details">
                  <div className="bolded-info">{t('my_access')}</div>
                  <div className="colored-info">
                    {capitalizeFirstLetter(
                      t(fleet.sharedItemDetails.myRole.toLowerCase()),
                    )}
                  </div>
                </Card>
              </div>
              <div className="col-xxs-6">
                <Card className="access-sharing-equipment-details">
                  <div className="bolded-info">{t('shared_with')}</div>
                  {!!fleet.sharedWith.length ? (
                    <div>
                      {t('shared_with_users', {
                        users: fleet.sharedWith.length,
                      })}
                    </div>
                  ) : null}
                </Card>
              </div>
            </div>

            <div className="more-details-button">
              <EllipsisMenu actions={actions} />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default SharedFleetsRow;
